"use client";

import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Button, Input, TimePicker, Switch, DatePicker, Radio, Tag, message } from "antd";
import {
  CheckOutlined,
  PlusOutlined,
  CloseOutlined,
  CameraOutlined,
  CalendarOutlined,
  SyncOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import useAuthSession from "../../../authPatient/hooks/useAuthSession";
import useMedications from "../../hooks/useMedications";
import useMedicationsDatabase from "../../hooks/useMedicationsDatabase";
import { createMedication, updateMedication } from "../../redux/thunks/medicationThunks";
import { applyMedicationActorPolicy } from "../../functions/medicationPayloadPolicy";
import "./MedicationTab.css";

/* ============ CONSTANTS ============ */
const TrackingMode = {
  DAILY: "daily",
  SPECIFIC_DAYS: "specificDays",
  LIFETIME: "lifetime",
};

const DAYS_OF_WEEK = [
  { value: 1, label: "Lun", fullLabel: "Lundi" },
  { value: 2, label: "Mar", fullLabel: "Mardi" },
  { value: 3, label: "Mer", fullLabel: "Mercredi" },
  { value: 4, label: "Jeu", fullLabel: "Jeudi" },
  { value: 5, label: "Ven", fullLabel: "Vendredi" },
  { value: 6, label: "Sam", fullLabel: "Samedi" },
  { value: 0, label: "Dim", fullLabel: "Dimanche" },
];

/* ============ HELPERS ============ */
const toSlug = (str = "", fb = "") => {
  let s = (str || "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9-_ ]+/g, "")
    .trim()
    .replace(/\s+/g, "-");
  if (!s || /^\d/.test(s)) s = `med-${fb || "x"}`;
  return s;
};

const bem = (b, m) => (m ? `${b} ${b}--${m}` : b);

const hhmmToIsoOnDate = (dateYYYYMMDD, hhmm) => {
  if (!dateYYYYMMDD || !hhmm) return null;
  return `${dateYYYYMMDD}T${hhmm}:00+01:00`;
};

const isEmpty = (v) => v === null || v === undefined || String(v).trim() === "";

const extractErrorMessage = (errLike, fallback = "Erreur lors de l'enregistrement.") => {
  if (!errLike) return fallback;
  if (typeof errLike?.message === "string" && errLike.message.trim()) return errLike.message;
  if (typeof errLike === "string" && errLike.trim()) return errLike;
  if (typeof errLike?.payload === "string" && errLike.payload.trim()) return errLike.payload;
  if (typeof errLike?.error?.message === "string" && errLike.error.message.trim()) {
    return errLike.error.message;
  }
  const m = errLike?.payload?.message || errLike?.message;
  if (Array.isArray(m) && m.length) return String(m[0]);
  if (typeof m === "string" && m.trim()) return m;
  return fallback;
};

const getTrackingLabel = (tracking, t) => {
  if (!tracking) return t("medicationTab.tracking.daily", { defaultValue: "Tous les jours" });

  switch (tracking.mode) {
    case TrackingMode.DAILY:
      return t("medicationTab.tracking.daily", { defaultValue: "Tous les jours" });
    case TrackingMode.LIFETIME:
      return t("medicationTab.tracking.lifetime", { defaultValue: "À vie" });
    case TrackingMode.SPECIFIC_DAYS: {
      const order = [1, 2, 3, 4, 5, 6, 0];
      const dayLabels = [...(tracking.selectedDays || [])]
        .sort((a, b) => order.indexOf(a) - order.indexOf(b))
        .map((d) => DAYS_OF_WEEK.find((day) => day.value === d)?.label || "")
        .filter(Boolean)
        .join(", ");
      return dayLabels || t("medicationTab.tracking.specificDays", { defaultValue: "Jours spécifiques" });
    }
    default:
      return t("medicationTab.tracking.daily", { defaultValue: "Tous les jours" });
  }
};

const getDefaultTracking = () => ({
  mode: TrackingMode.DAILY,
  selectedDays: [],
  startDate: null,
  endDate: null,
});

/**
 * Extrait HH:mm depuis une date ISO ou un objet Date
 */
const extractHHMM = (dateStr) => {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const h = String(d.getHours()).padStart(2, "0");
    const m = String(d.getMinutes()).padStart(2, "0");
    return `${h}:${m}`;
  } catch {
    return null;
  }
};

/* ============ UPLOADS ============ */
const gatewayBase = String(import.meta?.env?.VITE_GATEWAY_URL || "")
  .trim()
  .replace(/\/+$/, "");
const gatewayOrigin = gatewayBase ? gatewayBase.replace(/\/api\/?$/, "") : "";

const UPLOADS_BASE =
  import.meta?.env?.VITE_MEDICATION_UPLOADS_URL ||
  (gatewayOrigin ? `${gatewayOrigin}/api/medication/uploads` : "http://localhost:3010/uploads");

const resolveImageUrl = (img) => {
  if (!img) return "";
  const value = String(img).trim();
  if (!value || value === "null" || value === "undefined") return "";
  if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("data:")) {
    return value;
  }
  if (value.startsWith("/api/")) {
    return gatewayOrigin ? `${gatewayOrigin}${value}` : value;
  }
  const base = String(UPLOADS_BASE).replace(/\/+$/, "");
  let filename = value.replace(/^\/+/, "");
  if (filename.startsWith("uploads/")) {
    filename = filename.slice("uploads/".length);
  }
  return `${base}/${filename}`;
};

/* ============ COMPONENT ============ */
export default function MedicationTab({
  onClose,
  editMedication = null,
  userId: overrideUserId = null,
  onCreated,
  doctorMode = false,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { effectiveUserId } = useAuthSession({ auto: true });
  const userId = overrideUserId ?? effectiveUserId;

  const { loadAll, loadArchived, loading } = useMedications(userId);
  const {
    searchResults,
    searchLoading,
    search: searchMedicationsDB,
    clearSearch,
  } = useMedicationsDatabase();

  // Mode édition
  const isEditMode = !!editMedication;
  const editId = editMedication?._id || editMedication?.id || null;
  const [items, setItems] = useState([]);

  const [withReminders, setWithReminders] = useState(false);
  const [name, setName] = useState("");
  const [dose, setDose] = useState(1);
  const [quantity, setQuantity] = useState("");
  const [medicationQuantity, setMedicationQuantity] = useState("");
  const [times, setTimes] = useState([null]);

  const [trackingMode, setTrackingMode] = useState(TrackingMode.DAILY);
  const [selectedDays, setSelectedDays] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const nameRef = useRef("");
  const timesRef = useRef([null]);
  const trackingRef = useRef(getDefaultTracking());

  const [fieldErrors, setFieldErrors] = useState({
    name: "",
    times: "",
    startDate: "",
    endDate: "",
    selectedDays: "",
  });

  const [status, setStatus] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const fileRef = useRef(null);

  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const searchTimeout = useRef(null);
  const dropdownRef = useRef(null);

  // ⏱️ Ref pour nettoyer les écouteurs du panneau TimePicker
  const timePanelCleanupRef = useRef(null);

  // ⏱️ Index du TimePicker actuellement ouvert (null = tous fermés)
  const [openTimeIdx, setOpenTimeIdx] = useState(null);
  // ⏱️ true si l'heure a été choisie pendant cette ouverture (ou si une valeur existait déjà)
  const hourPickedRef = useRef(false);
  // ⏱️ Timer de fermeture automatique
  const autoCloseTimerRef = useRef(null);

  /* ============ POPULATE FORM IN EDIT MODE ============ */
  useEffect(() => {
    if (!editMedication) return;

    const med = editMedication;

    // Name
    const medName = med.name || "";
    nameRef.current = medName;
    setName(medName);
    setSearchTerm(medName);

    // Alarm
    setWithReminders(!!med.alarmOn);

    // Times
    const medTimes = Array.isArray(med.times) ? med.times : [];
    const parsedTimes = medTimes.map((t0) => {
      const hhmm = extractHHMM(t0);
      if (!hhmm) return null;
      return dayjs(hhmm, "HH:mm");
    });

    setDose(parsedTimes.length || 1);
    setTimes(parsedTimes.length > 0 ? parsedTimes : [null]);
    timesRef.current = parsedTimes.length > 0 ? parsedTimes : [null];

    // Tracking
    const tracking = med.tracking || {};
    const mode = tracking.mode || TrackingMode.DAILY;
    const days = Array.isArray(tracking.selectedDays) ? [...tracking.selectedDays] : [];

    setTrackingMode(mode);
    setSelectedDays(days);

    // Dates
    const sDate = med.startDate ? dayjs(med.startDate) : null;
    const eDate = med.endDate ? dayjs(med.endDate) : null;

    setStartDate(sDate);
    setEndDate(mode === TrackingMode.LIFETIME ? null : eDate);

    trackingRef.current = {
      mode,
      selectedDays: days,
      startDate: sDate?.format("YYYY-MM-DD") || null,
      endDate: mode === TrackingMode.LIFETIME ? null : eDate?.format("YYYY-MM-DD") || null,
    };

    // Image
    if (med.image) {
      const url = resolveImageUrl(med.image);
      if (url) {
        setPhotoUrl(url);
        // photoFile reste null car c'est une URL existante
        setPhotoFile(null);
      }
    }
  }, [editMedication]);

  /* ============ EFFECTS ============ */
  useEffect(() => {
    if (!userId) return;
    loadAll();
    loadArchived();
  }, [userId, loadAll, loadArchived]);

  useEffect(() => {
    nameRef.current = name;
  }, [name]);

  useEffect(() => {
    timesRef.current = times;
  }, [times]);

  useEffect(() => {
    trackingRef.current = {
      mode: trackingMode,
      selectedDays,
      startDate: startDate?.format?.("YYYY-MM-DD") || null,
      endDate: endDate?.format?.("YYYY-MM-DD") || null,
    };
  }, [trackingMode, selectedDays, startDate, endDate]);

  useEffect(() => {
    return () => {
      // Ne pas révoquer les URLs d'images du serveur
      if (photoUrl && photoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(photoUrl);
      }
    };
  }, [photoUrl]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDropdown]);

  useEffect(() => {
    setTimes((prev) => {
      const cur = Array.isArray(prev) ? prev : [];
      const d = Math.max(1, Number(dose) || 1);
      if (cur.length === d) return cur;
      if (d > cur.length) return [...cur, ...Array(d - cur.length).fill(null)];
      return cur.slice(0, d);
    });
  }, [dose]);

  // Nettoyage global (écouteurs + timer) au démontage du composant
  useEffect(() => {
    return () => {
      if (timePanelCleanupRef.current) {
        timePanelCleanupRef.current();
        timePanelCleanupRef.current = null;
      }
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, []);

  /* ============ HANDLERS ============ */
  const openPicker = () => fileRef.current?.click();

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      message.error(t("medicationTab.errors.invalidImage"));
      return;
    }

    if (photoUrl && photoUrl.startsWith("blob:")) {
      URL.revokeObjectURL(photoUrl);
    }
    const url = URL.createObjectURL(file);

    setPhotoUrl(url);
    setPhotoFile(file);
    e.target.value = "";
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    nameRef.current = value;

    setName(value);
    setSearchTerm(value);
    setFieldErrors((er) => ({ ...er, name: "" }));

    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (!value.trim()) {
      setShowDropdown(false);
      clearSearch();
      return;
    }

    searchTimeout.current = setTimeout(() => {
      searchMedicationsDB({ q: value, limit: 20 });
      setShowDropdown(true);
    }, 300);
  };

  const downloadImageAsFile = async (imageUrl, medicationName) => {
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error("Image download failed");

      const blob = await response.blob();
      const filename = `${medicationName.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.jpg`;
      return new File([blob], filename, { type: blob.type });
    } catch {
      return null;
    }
  };

  const filteredResults = useMemo(() => {
    if (!searchTerm || !searchResults || searchResults.length === 0) {
      return searchResults || [];
    }

    const searchLower = searchTerm.toLowerCase().trim();

    return (searchResults || []).filter((med) => {
      const dci = (med.dci || "").toLowerCase();
      const nomCommercial = (med.nom_commercial || "").toLowerCase();
      const nm = (med.name || "").toLowerCase();
      const laboratoire = (med.laboratoire || "").toLowerCase();

      return (
        dci.includes(searchLower) ||
        nomCommercial.includes(searchLower) ||
        nm.includes(searchLower) ||
        laboratoire.includes(searchLower)
      );
    });
  }, [searchResults, searchTerm]);

  const selectMedication = async (medication) => {
    const medicationName = medication.dci || medication.nom_commercial || medication.name || "";

    nameRef.current = medicationName;
    setName(medicationName);
    setSearchTerm(medicationName);
    setShowDropdown(false);

    if (
      medication.image_url &&
      medication.image_url !== "https://via.placeholder.com/44x44.png?text=?"
    ) {
      const file = await downloadImageAsFile(medication.image_url, medicationName);
      if (file) {
        if (photoUrl && photoUrl.startsWith("blob:")) {
          URL.revokeObjectURL(photoUrl);
        }
        const url = URL.createObjectURL(file);
        setPhotoUrl(url);
        setPhotoFile(file);
      }
    }

    message.success(t("medicationTab.toast.medicationSelected", { defaultValue: "Médicament sélectionné" }));
  };

  const handleTimeChange = (idx, value) => {
    const current = Array.isArray(timesRef.current) ? [...timesRef.current] : [];
    current[idx] = value;
    timesRef.current = current;

    setTimes(current);
    setFieldErrors((e) => ({ ...e, times: "" }));
  };

  /**
   * ⏱️ Appelé quand le panneau d'un TimePicker s'ouvre/se ferme.
   *
   * À l'ouverture, on attache deux écouteurs :
   *  - clic sur une HEURE  → on marque l'heure comme choisie + on scrolle
   *                          la colonne des minutes vers la valeur sélectionnée
   *  - clic sur une MINUTE → (si l'heure est choisie) on ferme automatiquement
   *                          le panneau, sans avoir besoin de cliquer sur "OK".
   *                          La valeur est déjà enregistrée via `onSelect`.
   */
  const handleTimeOpenChange = useCallback((idx, open) => {
    // Nettoyage des écouteurs / timers précédents
    if (timePanelCleanupRef.current) {
      timePanelCleanupRef.current();
      timePanelCleanupRef.current = null;
    }
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }

    if (!open) {
      setOpenTimeIdx((prev) => (prev === idx ? null : prev));
      return;
    }

    setOpenTimeIdx(idx);

    // Si une valeur existe déjà, un simple clic sur une minute suffit pour fermer
    hourPickedRef.current = !!(Array.isArray(timesRef.current) && timesRef.current[idx]);

    // Laisser AntD rendre le panneau dans le DOM avant de le cibler
    setTimeout(() => {
      const panel = document.querySelector(
        ".apm-time-popup:not(.ant-picker-dropdown-hidden) .ant-picker-time-panel"
      );
      if (!panel) return;

      const columns = panel.querySelectorAll(".ant-picker-time-panel-column");
      const hourColumn = columns[0];
      const minuteColumn = columns[1];
      if (!hourColumn || !minuteColumn) return;

      const handleHourClick = (e) => {
        const cell = e.target.closest(".ant-picker-time-panel-cell");
        if (!cell) return;

        hourPickedRef.current = true;

        // On attend le prochain tick pour laisser AntD mettre à jour
        // la classe "selected" sur la colonne des minutes
        setTimeout(() => {
          const selectedMinute = minuteColumn.querySelector(
            ".ant-picker-time-panel-cell-selected"
          );
          if (selectedMinute) {
            selectedMinute.scrollIntoView({ block: "center", behavior: "smooth" });
          }
        }, 0);
      };

      const handleMinuteClick = (e) => {
        const cell = e.target.closest(".ant-picker-time-panel-cell");
        if (!cell) return;

        // Fermer uniquement si l'heure a déjà été choisie
        if (!hourPickedRef.current) return;

        // Petit délai : laisse `onSelect` enregistrer la valeur
        // et permet de voir la minute sélectionnée avant la fermeture
        if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = setTimeout(() => {
          setOpenTimeIdx(null);
          autoCloseTimerRef.current = null;
        }, 150);
      };

      hourColumn.addEventListener("click", handleHourClick);
      minuteColumn.addEventListener("click", handleMinuteClick);

      timePanelCleanupRef.current = () => {
        hourColumn.removeEventListener("click", handleHourClick);
        minuteColumn.removeEventListener("click", handleMinuteClick);
      };
    }, 30);
  }, []);

  const handleTrackingModeChange = (e) => {
    const newMode = e.target.value;
    setTrackingMode(newMode);

    if (newMode === TrackingMode.LIFETIME) {
      setEndDate(null);
    }

    if (newMode !== TrackingMode.SPECIFIC_DAYS) {
      setSelectedDays([]);
    }

    setFieldErrors((e) => ({ ...e, endDate: "", selectedDays: "" }));
  };

  const handleDayToggle = (dayValue) => {
    setSelectedDays((prev) => {
      if (prev.includes(dayValue)) {
        return prev.filter((d) => d !== dayValue);
      }
      return [...prev, dayValue];
    });
    setFieldErrors((e) => ({ ...e, selectedDays: "" }));
  };

  const handleQuickSelect = (type) => {
    switch (type) {
      case "weekdays":
        setSelectedDays([1, 2, 3, 4, 5]);
        break;
      case "weekend":
        setSelectedDays([0, 6]);
        break;
      case "alternate":
        setSelectedDays([1, 3, 5]);
        break;
      case "all":
        setSelectedDays(selectedDays.length === 7 ? [] : [0, 1, 2, 3, 4, 5, 6]);
        break;
      default:
        break;
    }
    setFieldErrors((e) => ({ ...e, selectedDays: "" }));
  };

  const resetForm = () => {
    setWithReminders(false);
    nameRef.current = "";
    setName("");
    setDose(1);
    setQuantity("");
    setMedicationQuantity("");
    timesRef.current = [null];
    setTimes([null]);
    setOpenTimeIdx(null);

    setTrackingMode(TrackingMode.DAILY);
    setSelectedDays([]);
    setStartDate(null);
    setEndDate(null);
    trackingRef.current = getDefaultTracking();

    setFieldErrors({ name: "", times: "", startDate: "", endDate: "", selectedDays: "" });

    if (photoUrl && photoUrl.startsWith("blob:")) {
      URL.revokeObjectURL(photoUrl);
    }
    setPhotoUrl(null);
    setPhotoFile(null);

    setShowDropdown(false);
    setSearchTerm("");
    clearSearch();
  };

  const showOverlay = (type, msg) => {
    setStatus(type);
    setStatusMessage(msg || "");

    setTimeout(() => {
      setStatus("");
      setStatusMessage("");
      if (type === "success" && typeof onClose === "function") onClose();
    }, 3000);
  };

  const validateCurrentRequired = () => {
    const nextErrors = { name: "", times: "", startDate: "", endDate: "", selectedDays: "" };

    const label = (nameRef.current || "").trim();
    if (isEmpty(label)) {
      nextErrors.name = t("medicationTab.validation.nameRequired");
    }

    const currentTimes = Array.isArray(timesRef.current) ? timesRef.current : [];
    const missingTimes = currentTimes.some((tt) => !tt);
    if (missingTimes) {
      nextErrors.times = t("medicationTab.validation.timesRequired", { count: dose });
    }

    const tr = trackingRef.current;

    if (!tr.startDate) {
      nextErrors.startDate = t("medicationTab.validation.startDateRequired", {
        defaultValue: "Date de début requise",
      });
    }

    if (tr.mode !== TrackingMode.LIFETIME && !tr.endDate) {
      nextErrors.endDate = t("medicationTab.validation.endDateRequired", {
        defaultValue: "Date de fin requise",
      });
    }

    if (tr.mode === TrackingMode.SPECIFIC_DAYS && (!tr.selectedDays || tr.selectedDays.length === 0)) {
      nextErrors.selectedDays = t("medicationTab.validation.daysRequired", {
        defaultValue: "Sélectionnez au moins un jour",
      });
    }

    setFieldErrors(nextErrors);

    const ok =
      !nextErrors.name &&
      !nextErrors.times &&
      !nextErrors.startDate &&
      !nextErrors.endDate &&
      !nextErrors.selectedDays;

    if (!ok) {
      message.error(t("medicationTab.validation.fillAll"));
      return { ok: false };
    }

    return { ok: true, label, times: currentTimes, tracking: tr };
  };

  const buildPayloadFromCurrentForm = () => {
    const v = validateCurrentRequired();
    if (!v.ok) return null;

    const startDateStr = v.tracking.startDate;
    const endDateStr = v.tracking.endDate || undefined;

    const cleanTimes = (v.times || []).filter(Boolean).map((tt) => tt.format("HH:mm"));
    if (cleanTimes.length === 0) return null;

    const isoTimes = cleanTimes.map((hhmm) => hhmmToIsoOnDate(startDateStr, hhmm)).filter(Boolean);

    // ✅ Calculé ici directement, pas de dépendance externe
    const medQtyNumber = Number(medicationQuantity) || 0;
    const doseCount = Number(dose) || 1;
    const daysCovered = medQtyNumber > 0 ? Math.floor(medQtyNumber / doseCount) : 0;

    return {
      name: v.label,
      amount: Number(isoTimes.length),
      times: isoTimes,
      startDate: startDateStr,
      endDate: endDateStr,
      tracking: {
        mode: v.tracking.mode,
        selectedDays: v.tracking.mode === TrackingMode.SPECIFIC_DAYS ? v.tracking.selectedDays : [],
      },
      alarmOn: doctorMode ? false : Boolean(withReminders),
      image: photoFile || undefined,

      quantity: quantityRef.current.trim() || undefined,
      medicationQuantity: Number(medicationQuantityRef.current) || 0,
      estimatedDaysCovered: daysCovered,
    };
  };

  const addCurrentToList = () => {
    if (isEditMode) return; // Pas de liste en mode édition

    const v = validateCurrentRequired();
    if (!v.ok) return;

    const cleanTimes = (v.times || []).filter(Boolean).map((tt) => tt.format("HH:mm"));

    const payload = {
      id: Date.now(),
      name: v.label,
      dose: Number(cleanTimes.length) || 1,
      times: cleanTimes,
      tracking: {
        mode: v.tracking.mode,
        selectedDays: v.tracking.selectedDays || [],
        startDate: v.tracking.startDate,
        endDate: v.tracking.endDate,
      },
      reminders: withReminders,
      alarmOn: withReminders,
      photoUrl: photoUrl || null,
      image: photoFile || null,
      quantity: quantity.trim() || "",
      medicationQuantity: Number(medicationQuantity) || 0,
      estimatedDaysCovered,
    };

    setItems((a) => [payload, ...a]);
    resetForm();
    message.success(t("medicationTab.toast.addedToList"));
  };

  const removeItem = (id) => setItems((a) => a.filter((x) => x.id !== id));

  const isCurrentFormEmpty = useMemo(() => {
    const anyTimeSelected = Array.isArray(timesRef.current)
      ? timesRef.current.some((tt) => !!tt)
      : false;

    const hasTrackingData = startDate || endDate || selectedDays.length > 0;

    return (
      !(nameRef.current || "").trim() &&
      !anyTimeSelected &&
      !hasTrackingData &&
      !photoFile &&
      withReminders === false &&
      dose === 1 &&
      trackingMode === TrackingMode.DAILY
    );
  }, [photoFile, withReminders, dose, trackingMode, startDate, endDate, selectedDays]);

  /* ============ SAVE (CREATE or UPDATE) ============ */
  const onSaveAll = useCallback(async () => {
    if (!userId) {
      const msg = t("medicationTab.errors.notAuthenticated");
      message.error(msg);
      showOverlay("error", msg);
      return;
    }

    // MODE ÉDITION : update le médicament
    if (isEditMode && editId) {
      try {
        const formPayload = buildPayloadFromCurrentForm();
        if (!formPayload) return;
        const payload = applyMedicationActorPolicy(formPayload, doctorMode);

        payload.image = photoFile || undefined;

        const result = await dispatch(
          updateMedication({
            userId,
            id: editId,
            payload,
            version: editMedication?.version,
          }),
        );

        if (updateMedication.fulfilled.match(result)) {
          resetForm();
          await Promise.all([loadAll(), loadArchived()]);
          showOverlay(
            "success",
            t("medicationTab.overlay.updated", { defaultValue: "Médicament mis à jour" })
          );
          onCreated?.();
          return;
        }

        if (updateMedication.rejected.match(result)) {
          const msg = extractErrorMessage(result, t("medicationTab.errors.saveFailed"));
          throw new Error(msg);
        }

        throw new Error(t("medicationTab.errors.unexpected"));
      } catch (e) {
        const msg = extractErrorMessage(e, t("medicationTab.errors.saveFailed"));
        message.error(msg);
        showOverlay("error", msg);
      }
      return;
    }

    // MODE LISTE : save multiple
    if (items.length > 0) {
      try {
        const ops = items.map(async (it) => {
          const {
            name,
            times = [],
            tracking = {},
            alarmOn,
            image,
            quantity,
            medicationQuantity,
          } = it;
          const startDateStr = tracking.startDate || null;
          const endDateStr = tracking.endDate || undefined;

          if (!name || !times.length || !startDateStr) {
            throw new Error(t("medicationTab.errors.incomplete", { name: name || "?" }));
          }

          if (tracking.mode !== TrackingMode.LIFETIME && !endDateStr) {
            throw new Error(t("medicationTab.errors.incomplete", { name: name || "?" }));
          }

          const isoTimes = times.map((tm) => hhmmToIsoOnDate(startDateStr, tm)).filter(Boolean);

          const payload = applyMedicationActorPolicy({
            name,
            amount: Number(isoTimes.length),
            quantity: quantity || undefined,
            medicationQuantity: Number(medicationQuantity) || 0,
            times: isoTimes,
            startDate: startDateStr,
            endDate: endDateStr,
            tracking: {
              mode: tracking.mode || TrackingMode.DAILY,
              selectedDays: tracking.selectedDays || [],
            },
            alarmOn: doctorMode ? false : Boolean(alarmOn),
            image: image || undefined,
          }, doctorMode);

          const result = await dispatch(createMedication({ userId, payload }));
          if (createMedication.fulfilled.match(result)) return result.payload;

          if (createMedication.rejected.match(result)) {
            const msg = extractErrorMessage(result, t("medicationTab.errors.saveFailed"));
            throw new Error(msg);
          }

          throw new Error(t("medicationTab.errors.unexpected"));
        });

        await Promise.all(ops);

        setItems([]);
        resetForm();
        await Promise.all([loadAll(), loadArchived()]);
        showOverlay("success", t("medicationTab.overlay.savedMany"));
        onCreated?.();
      } catch (e) {
        const msg = extractErrorMessage(e, t("medicationTab.errors.saveFailed"));
        message.error(msg);
        showOverlay("error", msg);
      }
      return;
    }

    // MODE SINGLE CREATE
    try {
      if (isCurrentFormEmpty) {
        const msg = t("medicationTab.errors.nothingToSave");
        message.warning(msg);
        showOverlay("error", msg);
        return;
      }

      const formPayload = buildPayloadFromCurrentForm();
      if (!formPayload) return;
      const payload = applyMedicationActorPolicy(formPayload, doctorMode);

      payload.image = photoFile || undefined;

      const result = await dispatch(createMedication({ userId, payload }));

      if (createMedication.fulfilled.match(result)) {
        resetForm();
        await Promise.all([loadAll(), loadArchived()]);
        showOverlay("success", t("medicationTab.overlay.savedOne"));
        onCreated?.();
        return;
      }

      if (createMedication.rejected.match(result)) {
        const msg = extractErrorMessage(result, t("medicationTab.errors.saveFailed"));
        throw new Error(msg);
      }

      throw new Error(t("medicationTab.errors.unexpected"));
    } catch (e) {
      const msg = extractErrorMessage(e, t("medicationTab.errors.saveFailed"));
      message.error(msg);
      showOverlay("error", msg);
    }
  }, [
    userId,
    items,
    dispatch,
    loadAll,
    loadArchived,
    isCurrentFormEmpty,
    t,
    photoFile,
    isEditMode,
    editId,
    editMedication?.version,
    doctorMode,
    onCreated,
  ]);

  const currentSlug = toSlug(name, "current");

  const medicationQuantityNumber = Number(medicationQuantity) || 0;
  const dosePerDay = Number(dose) || 1;

  const estimatedDaysCovered =
    medicationQuantityNumber > 0
      ? Math.floor(medicationQuantityNumber / dosePerDay)
      : 0;

  const instanceId = useRef(Math.random().toString(36).slice(2));

  const quantityRef = useRef("");
  const medicationQuantityRef = useRef("");

  /* ============ RENDER ============ */
  return (
    <div className="apm-body">
      <div className="apm-subhead">
        {isEditMode
          ? t("medicationTab.ui.editTitle", { defaultValue: "Edit medication" })
          : t("medicationTab.ui.title")}
      </div>
      <p className="apm-helper">
        {isEditMode
          ? t("medicationTab.ui.editSubtitle", {
              defaultValue: "Modifiez les informations puis enregistrez.",
            })
          : t("medicationTab.ui.subtitle")}
      </p>

      <div className="apm-stage">
        <div className={`apm-two-col ${isEditMode ? "apm-edit-mode" : ""}`}>
          {/* ===== LISTE (masquée en mode édition) ===== */}
          {!isEditMode && (
            <div className="apm-list-card">
              <div className="apm-list-title">{t("medicationTab.ui.listTitle")}</div>

              {items.length === 0 && (
                <div className="apm-empty">
                  {t("medicationTab.ui.emptyLine1")} <b>+</b> {t("medicationTab.ui.emptyLine2")}
                  <br />
                  {t("medicationTab.ui.emptyLine3")} <b>{t("medicationTab.ui.saveBtn")}</b>.
                </div>
              )}

              <div className="apm-list">
                {items.map((m) => {
                  const slug = toSlug(m.name, m.id);
                  return (
                    <div key={m.id} className={bem("apm-med-item", slug)}>
                      <button
                        className={bem("apm-med-remove", slug)}
                        onClick={() => removeItem(m.id)}
                        aria-label={t("medicationTab.ui.removeItemAria", { name: m.name })}
                        type="button"
                      >
                        <CloseOutlined />
                      </button>

                      <div className={`${bem("apm-med-header", slug)} apm-med-header-inline`}>
                        {m.photoUrl && (
                          <div className="apm-med-photo apm-med-photo-inline">
                            <img src={m.photoUrl} alt={m.name} />
                          </div>
                        )}
                        <div className={bem("apm-med-name", slug)}>{m.name}</div>
                      </div>

                      <div className={bem("apm-med-meta", slug)}>
                        <div className={bem("apm-med-line", `${slug}-dose`)}>
                          <span className="apm-dot" />
                          <span className={bem("apm-med-dose", slug)}>
                            {m.dose}{" "}
                            {m.dose > 1
                              ? t("medicationTab.ui.dosesPerDay")
                              : t("medicationTab.ui.dosePerDay")}
                          </span>
                        </div>

                        {m.quantity && (
                          <div className={bem("apm-med-line", `${slug}-quantity`)}>
                            <span className="apm-dot" />
                            <span>Dosage : {m.quantity}</span>
                          </div>
                        )}

                        {m.medicationQuantity > 0 && (
                          <div className={bem("apm-med-line", `${slug}-box`)}>
                            <span className="apm-dot" />
                            <span>Boîte : {m.medicationQuantity} unité(s)</span>
                          </div>
                        )}

                        {m.estimatedDaysCovered > 0 && (
                          <div className={bem("apm-med-line", `${slug}-coverage`)}>
                            <span className="apm-dot" />
                            <span>Couvre environ {m.estimatedDaysCovered} jour(s)</span>
                          </div>
                        )}

                        {m.times?.length > 0 && (
                          <div className={bem("apm-med-times", slug)}>
                            {m.times.map((tm, idx) => (
                              <div key={idx} className={bem("apm-med-time", `${slug}-${idx + 1}`)}>
                                <span>{tm}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className={bem("apm-med-tracking", slug)}>
                          <span className="apm-tracking-badge">
                            {getTrackingLabel(m.tracking, t)}
                          </span>
                        </div>

                        {m.tracking?.startDate && (
                          <div className={bem("apm-med-duration", slug)}>
                            <span className={bem("apm-med-period", slug)}>
                              {m.tracking.startDate}
                              {m.tracking.mode !== TrackingMode.LIFETIME && m.tracking.endDate && (
                                <> → {m.tracking.endDate}</>
                              )}
                              {m.tracking.mode === TrackingMode.LIFETIME && <> → ∞</>}
                            </span>
                          </div>
                        )}

                        {m.reminders && (
                          <div className={bem("apm-med-chip", `${slug}-reminders`)}>
                            {t("medicationTab.ui.remindersOn")}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ===== FORMULAIRE ===== */}
          <div className={`apm-form-wrap ${isEditMode ? "apm-form-wrap--full" : ""}`}>
            <div className={bem("apm-form-card", currentSlug)}>
              {/* Rappels */}
              {!doctorMode && (
                <div className="apm-row space-between">
                  <span className="apm-label">{t("medicationTab.ui.reminderLabel")}</span>
                  <Switch checked={withReminders} onChange={(v) => setWithReminders(v)} />
                </div>
              )}

              {/* Nom du médicament */}
              <div className="apm-field">
                <label className="apm-label">{t("medicationTab.ui.nameLabel")}</label>
                <div className="apm-name-with-dropdown" ref={dropdownRef}>
                  <Input
                    placeholder={t("medicationTab.ui.namePlaceholder")}
                    value={name}
                    status={fieldErrors.name ? "error" : ""}
                    onChange={handleNameChange}
                    className="apm-input-with-suffix"
                    suffix={
                      photoUrl ? (
                        <div className="apm-photo-suffix">
                          <img src={photoUrl} alt={t("medicationTab.ui.photoAlt")} />
                          <button
                            className="apm-photo-suffix-clear"
                            onClick={() => {
                              if (photoUrl.startsWith("blob:")) {
                                URL.revokeObjectURL(photoUrl);
                              }
                              setPhotoUrl(null);
                              setPhotoFile(null);
                            }}
                            type="button"
                            aria-label={t("medicationTab.ui.removePhoto")}
                          >
                            <CloseOutlined />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="apm-camera-btn"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={openPicker}
                          title={t("medicationTab.ui.addPhoto")}
                        >
                          <CameraOutlined />
                        </button>
                      )
                    }
                  />

                  {showDropdown && searchTerm && (
                    <div className="medication-search-dropdown">
                      {searchLoading ? (
                        <div className="medication-search-loading">
                          {t("medicationTab.ui.searching", { defaultValue: "Recherche..." })}
                        </div>
                      ) : filteredResults && filteredResults.length > 0 ? (
                        <div className="medication-search-results">
                          {filteredResults.map((med) => (
                            <div
                              key={med._id || med.id}
                              className="medication-search-item"
                              onClick={() => selectMedication(med)}
                            >
                              <div className="medication-search-photo">
                                <img
                                  src={med.image_url || "https://via.placeholder.com/40x40.png?text=?"}
                                  alt={med.nom_commercial || med.name}
                                  onError={(e) => {
                                    e.currentTarget.src = "https://via.placeholder.com/40x40.png?text=?";
                                  }}
                                />
                              </div>
                              <div className="medication-search-info">
                                <div className="medication-search-name">
                                  {med.dci ||
                                    med.nom_commercial ||
                                    med.name ||
                                    t("medicationTab.ui.unknownMed", { defaultValue: "Médicament inconnu" })}
                                </div>
                                <div className="medication-search-details">
                                  {med.forme && <span>{med.forme}</span>}
                                  {med.dosage && <span> • {med.dosage}</span>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="medication-search-empty">
                          {t("medicationTab.ui.noResults", { defaultValue: "Aucun résultat trouvé" })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {fieldErrors.name && <div className="apm-error">{fieldErrors.name}</div>}

                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={onPickPhoto}
                  style={{ display: "none" }}
                />
              </div>

              {/* Doses et heures */}
              <div className="apm-grid-2">
                <div className="apm-field dose-field">
                  <label className="apm-label">{t("medicationTab.ui.doseLabel")}</label>
                  <div className="apm-dose">
                    <button
                      type="button"
                      className="apm-step"
                      onClick={() => setDose((d) => Math.max(1, d - 1))}
                      aria-label={t("medicationTab.ui.decreaseDose")}
                    >
                      −
                    </button>
                    <span className="apm-dose-value">{dose}</span>
                    <button
                      type="button"
                      className="apm-step"
                      onClick={() => setDose((d) => d + 1)}
                      aria-label={t("medicationTab.ui.increaseDose")}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="apm-field times-field">
                  <label className="apm-label">
                    {t("medicationTab.ui.timesLabel", { count: dose })}
                  </label>

                  <div className="apm-time-grid">
                    {times.map((tt, i) => (
                      <TimePicker
                        key={i}
                        style={{ width: "100%" }}
                        format="HH:mm"
                        value={tt}
                        status={fieldErrors.times ? "error" : ""}
                        open={openTimeIdx === i}
                        onOpenChange={(open) => handleTimeOpenChange(i, open)}
                        onSelect={(v) => handleTimeChange(i, v)}
                        onChange={(v) => handleTimeChange(i, v)}
                        popupClassName="apm-time-popup"
                        minuteStep={5}
                        showNow={false}
                        placeholder={t("medicationTab.ui.timePlaceholder", { index: i + 1 })}
                        inputReadOnly
                      />
                    ))}
                  </div>

                  {fieldErrors.times && <div className="apm-error">{fieldErrors.times}</div>}
                </div>
              </div>

              <div className="apm-grid-2">
                <div className="apm-field">
                  <label className="apm-label">Quantity</label>
                  <Input
                    placeholder="Ex: 500mg, 10ml"
                    defaultValue=""
                    onChange={(e) => {
                      quantityRef.current = e.target.value;
                    }}
                  />
                </div>

                <div className="apm-field">
                  <label className="apm-label">Box Quantity</label>
                  <Input
                    type="number"
                    min={0}
                    defaultValue=""
                    onChange={(e) => {
                      medicationQuantityRef.current = e.target.value;
                    }}
                  />
                </div>
              </div>

              {estimatedDaysCovered > 0 && (
                <div className="apm-consumption-info">
                  This box covers approximately {estimatedDaysCovered} day(s).
                </div>
              )}

              {/* ===== TRACKING SECTION ===== */}
              <div className="apm-field apm-tracking-section">
                <label className="apm-label">
                  {t("medicationTab.tracking.frequencyLabel", { defaultValue: "Fréquence de prise" })}
                </label>

                <Radio.Group
                  value={trackingMode}
                  onChange={handleTrackingModeChange}
                  className="apm-tracking-mode-group"
                  buttonStyle="solid"
                >
                  <Radio.Button value={TrackingMode.DAILY} className="apm-tracking-mode-btn">
                    <span>{t("medicationTab.tracking.daily", { defaultValue: "Tous les jours" })}</span>
                  </Radio.Button>
                  <Radio.Button value={TrackingMode.SPECIFIC_DAYS} className="apm-tracking-mode-btn">
                    <span>{t("medicationTab.tracking.specificDays", { defaultValue: "Jours spécifiques" })}</span>
                  </Radio.Button>
                  <Radio.Button value={TrackingMode.LIFETIME} className="apm-tracking-mode-btn">
                    <span>{t("medicationTab.tracking.lifetime", { defaultValue: "À vie" })}</span>
                  </Radio.Button>
                </Radio.Group>

                {trackingMode === TrackingMode.SPECIFIC_DAYS && (
                  <div className="apm-days-section">
                    <div className="apm-quick-select">
                      <Tag
                        className={`apm-quick-tag ${
                          JSON.stringify([...selectedDays].sort()) === JSON.stringify([1, 2, 3, 4, 5])
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => handleQuickSelect("weekdays")}
                      >
                        {t("medicationTab.tracking.weekdays", { defaultValue: "Semaine" })}
                      </Tag>
                      <Tag
                        className={`apm-quick-tag ${
                          JSON.stringify([...selectedDays].sort()) === JSON.stringify([0, 6])
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => handleQuickSelect("weekend")}
                      >
                        {t("medicationTab.tracking.weekend", { defaultValue: "Week-end" })}
                      </Tag>
                      <Tag
                        className={`apm-quick-tag ${
                          JSON.stringify([...selectedDays].sort()) === JSON.stringify([1, 3, 5])
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => handleQuickSelect("alternate")}
                      >
                        {t("medicationTab.tracking.alternate", { defaultValue: "1 jour sur 2" })}
                      </Tag>
                      <Tag
                        className={`apm-quick-tag ${selectedDays.length === 7 ? "selected" : ""}`}
                        onClick={() => handleQuickSelect("all")}
                      >
                        {t("medicationTab.tracking.allDays", { defaultValue: "Tous" })}
                      </Tag>
                    </div>

                    <div className="apm-days-grid">
                      {DAYS_OF_WEEK.map((day) => (
                        <div
                          key={day.value}
                          className={`apm-day-item ${selectedDays.includes(day.value) ? "selected" : ""}`}
                          onClick={() => handleDayToggle(day.value)}
                        >
                          <span className="apm-day-short">{day.label}</span>
                        </div>
                      ))}
                    </div>

                    {selectedDays.length > 0 && (
                      <div className="apm-days-summary">
                        {t("medicationTab.tracking.selectedCount", {
                          count: selectedDays.length,
                          defaultValue: `${selectedDays.length} jour(s) sélectionné(s)`,
                        })}
                      </div>
                    )}

                    {fieldErrors.selectedDays && (
                      <div className="apm-error">{fieldErrors.selectedDays}</div>
                    )}
                  </div>
                )}

                <div className="apm-dates-section">
                  <div className="apm-dates-grid">
                    <div className="apm-date-field">
                      <label className="apm-label-small">
                        {t("medicationTab.tracking.startDate", { defaultValue: "Date de début" })}
                      </label>
                      <DatePicker
                        value={startDate}
                        onChange={(date) => {
                          setStartDate(date);
                          setFieldErrors((e) => ({ ...e, startDate: "" }));
                        }}
                        format="YYYY-MM-DD"
                        placeholder={t("medicationTab.tracking.selectDate", {
                          defaultValue: "Sélectionner",
                        })}
                        style={{ width: "100%" }}
                        status={fieldErrors.startDate ? "error" : ""}
                        inputReadOnly
                      />
                      {fieldErrors.startDate && (
                        <div className="apm-error">{fieldErrors.startDate}</div>
                      )}
                    </div>

                    {trackingMode !== TrackingMode.LIFETIME ? (
                      <div className="apm-date-field">
                        <label className="apm-label-small">
                          {t("medicationTab.tracking.endDate", { defaultValue: "Date de fin" })}
                        </label>
                        <DatePicker
                          value={endDate}
                          onChange={(date) => {
                            setEndDate(date);
                            setFieldErrors((e) => ({ ...e, endDate: "" }));
                          }}
                          format="YYYY-MM-DD"
                          placeholder={t("medicationTab.tracking.selectDate", {
                            defaultValue: "Sélectionner",
                          })}
                          style={{ width: "100%" }}
                          status={fieldErrors.endDate ? "error" : ""}
                          disabledDate={(current) => startDate && current < startDate}
                          inputReadOnly
                        />
                        {fieldErrors.endDate && <div className="apm-error">{fieldErrors.endDate}</div>}
                      </div>
                    ) : (
                      <div className="apm-lifetime-indicator">
                        <FieldTimeOutlined className="apm-lifetime-icon" />
                        <span>
                          {t("medicationTab.tracking.lifetimeNote", {
                            defaultValue: "Prise indéfinie",
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="apm-tracking-description">
                  {trackingMode === TrackingMode.DAILY && (
                    <p>
                      {t("medicationTab.tracking.dailyDesc", {
                        defaultValue: "Le médicament sera à prendre tous les jours pendant la période définie.",
                      })}
                    </p>
                  )}
                  {trackingMode === TrackingMode.SPECIFIC_DAYS && (
                    <p>
                      {t("medicationTab.tracking.specificDaysDesc", {
                        defaultValue: "Le médicament sera à prendre uniquement les jours sélectionnés.",
                      })}
                    </p>
                  )}
                  {trackingMode === TrackingMode.LIFETIME && (
                    <p>
                      {t("medicationTab.tracking.lifetimeDesc", {
                        defaultValue:
                          "Le médicament sera à prendre tous les jours, sans date de fin. Vous pourrez l'archiver manuellement.",
                      })}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* FAB uniquement en mode création */}
            {!isEditMode && (
              <button
                className="apm-fab"
                onClick={addCurrentToList}
                title={t("medicationTab.ui.addToList")}
                type="button"
              >
                <PlusOutlined />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="apm-end">
        <p>{t("medicationTab.ui.disclaimer")}</p>
      </div>

      <div className="apm-actions end">
        {/* Bouton annuler en mode édition */}
        {isEditMode && (
          <Button
            onClick={() => {
              resetForm();
              if (typeof onClose === "function") onClose();
            }}
            style={{ marginRight: 8 }}
          >
            {t("medicationTab.ui.cancelBtn", { defaultValue: "Annuler" })}
          </Button>
        )}

        <Button
          type="primary"
          onClick={onSaveAll}
          disabled={
            isEditMode
              ? !userId || loading
              : (!items.length && isCurrentFormEmpty) || !userId || loading
          }
          loading={loading}
          icon={<CheckOutlined />}
        >
          {isEditMode
            ? t("medicationTab.ui.updateBtn", { defaultValue: "Mettre à jour" })
            : t("medicationTab.ui.saveBtn")}
        </Button>
      </div>

      {status && (
        <div className={`message-overlay ${status}`}>
          {statusMessage ||
            (status === "success"
              ? t("medicationTab.overlay.savedMany")
              : t("medicationTab.overlay.error"))}
        </div>
      )}
    </div>
  );
}
